import type { PostAuthor, PostClub } from "../models";

/**
 * The cast, ported from `MockFeedService.swift` and `USER_PROFILES.md`.
 *
 * Photo paths stay in the Swift asset-catalogue shape (`Characters/<person>/<file>`);
 * `assetUrl()` in ../assets.ts is what resolves them to something a browser can load.
 */

export const CURRENT_USER_AUTHOR: PostAuthor = {
  id: "user-001",
  displayName: "Alex Chen",
  profilePhotoURL: "Characters/alex/alex-profile",
  school: "UT Austin",
  isVerified: true,
};

export const MOCK_AUTHORS: PostAuthor[] = [
  {
    id: "u1",
    displayName: "Jamie Park",
    profilePhotoURL: "Characters/jamie/jamie-profile",
    school: "UT Austin",
    isVerified: true,
  },
  {
    id: "u2",
    displayName: "Maya Rodriguez",
    profilePhotoURL: "Characters/maya/maya-profile",
    school: "UT Austin",
    isVerified: true,
  },
  {
    id: "u3",
    displayName: "Tyler Washington",
    profilePhotoURL: "Characters/tyler/tyler-profile",
    school: "UT Austin",
    isVerified: false,
  },
  {
    id: "u4",
    displayName: "Sophie Liu",
    profilePhotoURL: "Characters/sophie/sophie-profile",
    school: "UT Austin",
    isVerified: true,
  },
  {
    id: "u5",
    displayName: "Darius Johnson",
    profilePhotoURL: "Characters/darius/darius-profile",
    school: "UT Austin",
    isVerified: true,
  },
  {
    id: "u6",
    displayName: "Priya Patel",
    profilePhotoURL: "Characters/priya/priya-profile",
    school: "UT Austin",
    isVerified: true,
  },
  {
    id: "u7",
    displayName: "Carlos Reyes",
    profilePhotoURL: "Characters/carlos/carlos-profile",
    school: "UT Austin",
    isVerified: false,
  },
  {
    id: "u8",
    displayName: "Brittany Okafor",
    profilePhotoURL: "Characters/brittany/brittany-profile",
    school: "UT Austin",
    isVerified: true,
  },
  // Deliberately has no photo — the initials-on-pastel avatar fallback needs a
  // real case in the seed, not a synthetic one.
  { id: "u9", displayName: "Ethan Nguyen", school: "UT Austin", isVerified: false },
  {
    id: "u10",
    displayName: "Zara Mitchell",
    profilePhotoURL: "Characters/zara/zara-profile",
    school: "UT Austin",
    isVerified: true,
  },
];

export const MOCK_CLUBS: PostClub[] = [
  { id: "club1", name: "ACM - CS Club", school: "UT Austin" },
  { id: "club2", name: "Freshman Council", school: "UT Austin" },
  { id: "club3", name: "Intramural Sports", school: "UT Austin" },
];

export function authorForId(id: string): PostAuthor {
  if (id === "user-001") return CURRENT_USER_AUTHOR;
  return MOCK_AUTHORS.find((a) => a.id === id) ?? MOCK_AUTHORS[0];
}

/** Whose posts surface in the match feed. */
export const MATCHED_USER_IDS = new Set(["u2", "u1", "u4", "u10", "u5", "u6", "u8"]);

export const USER_MAJORS: Record<string, string> = {
  u1: "Computer Science",
  u2: "Film Studies",
  u3: "Kinesiology",
  u4: "Psychology",
  u5: "Business",
  u6: "Biology",
  u7: "Marketing",
  u8: "Communications",
  u9: "Accounting",
  u10: "Political Science",
};

export const USER_ZODIACS: Record<string, string> = {
  u1: "Aries",
  u2: "Taurus",
  u3: "Gemini",
  u4: "Leo",
  u5: "Sagittarius",
  u6: "Virgo",
  u7: "Libra",
  u8: "Scorpio",
  u9: "Capricorn",
  u10: "Aquarius",
};

/**
 * Classmate scoping. Keyed on the *display code* rather than the course id,
 * matching the Swift original — `getSharedCourses` returns what a badge renders,
 * so anything comparing this to `Course.id` will silently miss.
 */
export const SHARED_COURSES: Record<string, string> = {
  u1: "CS 101",
  u2: "ENG 102",
  u4: "MATH 1B",
  u5: "ENG 102",
  u6: "MATH 1B",
  u8: "ENG 102",
  u10: "ENG 102",
};
